// T336 — the five "what's converting" tables as one file.
//
// The roadmap calls this the only export worth building, and the reason is in
// the shape: the page shows five rollups of the same applications cut five
// ways, and the question that outruns the screen is always a second cut —
// "jobindex in Denmark with v3". The page cannot show that and a spreadsheet
// can, so the honest export is the rows, long-format, with the dimension named
// in a column rather than five files the operator has to join by hand.
//
// Pure and separately exported so the header contract is testable. Handing a
// component a `download` button and asserting on a Blob proves nothing about
// what is inside it.

/** The five cuts the section renders, in the order it renders them. */
export const CONVERTING_DIMENSIONS = Object.freeze([
  { key: 'bySource', label: 'Source', valueOf: (r) => r.source },
  { key: 'byCountry', label: 'Country', valueOf: (r) => r.country },
  { key: 'byCompany', label: 'Company', valueOf: (r) => r.company },
  { key: 'byCvVersion', label: 'CV version', valueOf: (r) => (r.version == null ? null : `v${r.version}`) },
  { key: 'byRemote', label: 'Arrangement', valueOf: (r) => r.remote },
]);

export const CONVERTING_COLUMNS = Object.freeze([
  { key: 'dimension', label: 'Dimension' },
  { key: 'value', label: 'Value' },
  { key: 'applied', label: 'Applied' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'ghosted', label: 'Ghosted' },
  { key: 'advanceRatePct', label: 'Advance rate %' },
  { key: 'belowFloor', label: 'Below read floor' },
]);

/**
 * Flatten the rollups into one long-format table.
 *
 * `minN` marks rather than drops. The screen greys a thin row and withholds its
 * rate; dropping it from the file instead would hand the operator a
 * spreadsheet whose totals disagree with the page he exported it from. The
 * rate is still blanked — a 1-of-1 source is 100% and that is exactly the false
 * lead the floor exists to suppress — but the counts survive, because they are
 * facts.
 */
export function convertingRows(data = {}, minN = 3) {
  const out = [];
  for (const d of CONVERTING_DIMENSIONS) {
    for (const r of data[d.key] ?? []) {
      const applied = r.applied ?? 0;
      const thin = applied < minN;
      const value = d.valueOf(r);
      out.push({
        dimension: d.label,
        // Same sentinel the rollups use for an attribute-less row, so the file
        // and the page name the bucket identically.
        value: value == null || value === '' ? 'unknown' : String(value),
        applied,
        advanced: r.advanced ?? 0,
        ghosted: r.ghosted ?? 0,
        advanceRatePct: thin ? '' : (r.advanceRatePct ?? ''),
        belowFloor: thin ? 'yes' : '',
      });
    }
  }
  return out;
}
